import { Decimal, roundQuantity } from './money.js';
import { roofGeometry } from './roof.js';
/**
 * Обмерный план помещений и расчёт объёмов работ для сметы.
 *
 * Прораб на объекте вводит размеры помещений и проёмов, а объёмы (площади стен,
 * пола, длина плинтуса и т. п.) считаются здесь — одинаково в браузере и на
 * сервере. Сервер пересчитывает объёмы сам и не доверяет присланному числу:
 * иначе смета, собранная на старой версии фронтенда, разошлась бы с обмерами.
 *
 * Вся арифметика — Decimal: площадь 3,3 × 2,7 в number уже даёт хвост
 * 8.909999999999998, а объём × цена затем идёт в деньги.
 */
/** Какой обмеренный показатель даёт объём строки сметы. */
export const MEASURE_METRICS = [
    'FLOOR_AREA',
    'CEILING_AREA',
    'CEILING_SLOPE_AREA',
    'WALL_AREA',
    'PERIMETER',
    'BASEBOARD_LENGTH',
    'SLOPES_LENGTH',
    'OPENINGS_AREA',
];
export const MEASURE_METRIC_INFO = {
    FLOOR_AREA: { label: 'Площадь пола', unit: 'м²' },
    // У мансарды — настоящая площадь со скатами, она больше площади пола.
    CEILING_AREA: { label: 'Площадь потолка', unit: 'м²' },
    CEILING_SLOPE_AREA: { label: 'Площадь скатов потолка', unit: 'м²' },
    WALL_AREA: { label: 'Площадь стен за вычетом проёмов', unit: 'м²' },
    PERIMETER: { label: 'Периметр помещения', unit: 'м' },
    BASEBOARD_LENGTH: { label: 'Плинтус (периметр без дверей)', unit: 'м' },
    SLOPES_LENGTH: { label: 'Откосы окон и дверей', unit: 'м' },
    OPENINGS_AREA: { label: 'Площадь проёмов', unit: 'м²' },
};
/**
 * Направление стены контура на плане, в градусах по часовой стрелке от
 * «вправо»: 0 — вправо, 90 — вниз, 180 — влево, 270 — вверх. Только кратные
 * 45°: этого хватает на эркеры и скошенные углы, а синус и косинус таких
 * углов считаются точно — площадь не получает хвостов от тригонометрии.
 */
export const WALL_DIRECTIONS = [0, 45, 90, 135, 180, 225, 270, 315];
/**
 * Число из поля ввода: запятая допустима (российская раскладка), пустое или
 * некорректное значение — null. Отрицательные размеры не бывают.
 */
export function parseMeasure(value) {
    if (value === null || value === undefined)
        return null;
    const normalized = value.trim().replace(',', '.');
    if (!/^\d+(\.\d+)?$/.test(normalized))
        return null;
    return new Decimal(normalized);
}
const ZERO = new Decimal(0);
function orZero(value) {
    return parseMeasure(value) ?? ZERO;
}
const HALF_SQRT2 = new Decimal('0.5').sqrt();
/**
 * Единичный вектор направления. Для кратных 90° — точные 0 и ±1: Decimal.cos
 * дал бы 1e-30 вместо нуля, и площадь прямоугольного контура получила бы хвост.
 */
function unitVector(direction) {
    const one = new Decimal(1);
    const zero = new Decimal(0);
    const h = HALF_SQRT2;
    switch (direction) {
        case 0: return [one, zero];
        case 45: return [h, h];
        case 90: return [zero, one];
        case 135: return [h.negated(), h];
        case 180: return [one.negated(), zero];
        case 225: return [h.negated(), h.negated()];
        case 270: return [zero, one.negated()];
        case 315: return [h, h.negated()];
    }
}
/** Контур считается замкнутым, если конец последней стены не дальше 1 см от начала. */
export const CONTOUR_TOLERANCE = new Decimal('0.01');
/**
 * Геометрия контура. Невязка не «подгоняется»: площадь считается по
 * фактическим вершинам с замыканием на начало, а прорабу показывается,
 * на сколько не сошлись размеры, — это почти всегда ошибка в обмере.
 */
export function contourGeometry(walls) {
    const vertices = [];
    let x = new Decimal(0);
    let y = new Decimal(0);
    let perimeter = new Decimal(0);
    for (const wall of walls) {
        vertices.push([x, y]);
        const len = orZero(wall.length);
        const [ux, uy] = unitVector(wall.direction);
        x = x.plus(ux.times(len));
        y = y.plus(uy.times(len));
        perimeter = perimeter.plus(len);
    }
    // Формула площади Гаусса по вершинам, с замыканием последней на первую.
    let twice = new Decimal(0);
    for (let i = 0; i < vertices.length; i++) {
        const [x1, y1] = vertices[i];
        const [x2, y2] = vertices[(i + 1) % vertices.length];
        twice = twice.plus(x1.times(y2).minus(x2.times(y1)));
    }
    const signedArea = twice.dividedBy(2);
    const gap = x.pow(2).plus(y.pow(2)).sqrt();
    return {
        vertices,
        end: [x, y],
        gap,
        closed: gap.lessThanOrEqualTo(CONTOUR_TOLERANCE),
        signedArea,
        area: signedArea.abs(),
        perimeter,
    };
}
/** Контур задан, если в нём хотя бы три стены — меньше не образует помещение. */
export function hasContour(room) {
    return Array.isArray(room.contour) && room.contour.length >= 3;
}
/** Прямоугольник как контур: обход по часовой стрелке от левого верхнего угла. */
export function rectangleContour(length, width) {
    return [
        { length, direction: 0 },
        { length: width, direction: 90 },
        { length, direction: 180 },
        { length: width, direction: 270 },
    ];
}
/**
 * Стены помещения для схемы и скатов. У помещения с ручными площадью и
 * периметром формы нет — выдумывать прямоугольник нельзя, поэтому null.
 */
export function roomWalls(room) {
    if (hasContour(room))
        return room.contour;
    // Контур включён, но ещё не нарисован — рисовать нечего.
    if (room.contour != null)
        return null;
    if (room.floorAreaOverride != null || room.perimeterOverride != null)
        return null;
    const length = parseMeasure(room.length);
    const width = parseMeasure(room.width);
    if (!length || !width || length.isZero() || width.isZero())
        return null;
    return rectangleContour(room.length, room.width);
}
/**
 * Горизонтальный отступ участка ската. По потолку замеряют наклонную длину:
 * отступ — катет прямоугольного треугольника с гипотенузой-длиной и
 * катетом-перепадом высот.
 */
function slopeRun(length, rise, along) {
    if (along === 'FLOOR')
        return length.isZero() ? null : length;
    if (length.lessThanOrEqualTo(rise))
        return null;
    return length.pow(2).minus(rise.pow(2)).sqrt();
}
/** Разбор ската с проверкой: ошибка — понятная прорабу фраза, скат с ошибкой в расчёт не идёт. */
function parseSlope(slope, height, wallCount) {
    if (slope.wall < 1 || slope.wall > wallCount)
        return { error: 'Такой стены нет в помещении' };
    const knee = parseMeasure(slope.kneeHeight);
    const length = parseMeasure(slope.length);
    if (!knee || !length)
        return { error: 'Укажите высоту стены и размер ската' };
    if (knee.greaterThanOrEqualTo(height))
        return { error: 'Стена под скатом должна быть ниже высоты помещения' };
    const breakHeight = slope.breakHeight != null ? parseMeasure(slope.breakHeight) : null;
    if (slope.breakHeight != null) {
        if (!breakHeight)
            return { error: 'Укажите высоту излома' };
        if (breakHeight.lessThanOrEqualTo(knee) || breakHeight.greaterThanOrEqualTo(height)) {
            return { error: 'Излом должен быть выше стены под скатом и ниже потолка' };
        }
    }
    const top = breakHeight ?? height;
    const rise = top.minus(knee);
    const run = slopeRun(length, rise, slope.measuredAlong);
    if (!run) {
        return {
            error: slope.measuredAlong === 'CEILING'
                ? `Длина ската по потолку должна быть больше перепада высот (${rise.toString()} м)`
                : 'Укажите отступ ската по полу',
        };
    }
    const k = rise.dividedBy(run);
    const segments = [{ wall: slope.wall, h0: knee, start: new Decimal(0), k }];
    if (breakHeight) {
        const upperLength = parseMeasure(slope.breakLength);
        if (!upperLength)
            return { error: 'Укажите размер верхнего участка ската' };
        const upperRise = height.minus(breakHeight);
        const upperRun = slopeRun(upperLength, upperRise, slope.measuredAlong);
        if (!upperRun) {
            return {
                error: slope.measuredAlong === 'CEILING'
                    ? `Верхний участок по потолку должен быть длиннее перепада высот (${upperRise.toString()} м)`
                    : 'Укажите отступ верхнего участка по полу',
            };
        }
        const upperK = upperRise.dividedBy(upperRun);
        // Потолок — нижняя огибающая плоскостей, поэтому излом вверх (сначала
        // полого, потом круто) так не описать: крутая плоскость «победила» бы.
        if (upperK.greaterThanOrEqualTo(k))
            return { error: 'Верхний участок ломаного ската должен быть положе нижнего' };
        segments.push({ wall: slope.wall, h0: breakHeight, start: run, k: upperK });
    }
    return { segments };
}
/** Ошибки ввода скатов по порядку room.slopes: null — скат в порядке. */
export function slopeErrors(room) {
    const slopes = room.slopes ?? [];
    const walls = roomWalls(room);
    const height = parseMeasure(room.height);
    return slopes.map((slope) => {
        if (!walls)
            return 'Скаты считаются по форме помещения — задайте длину и ширину или нарисуйте контур';
        if (!height || height.isZero())
            return 'Укажите высоту помещения — до неё поднимается скат';
        const parsed = parseSlope(slope, height, walls.length);
        return 'error' in parsed ? parsed.error : null;
    });
}
/** Геометрия мансарды; null — у помещения нет скатов (или формы, по которой их считать). */
export function roomRoof(room) {
    if (!room.slopes?.length)
        return null;
    const walls = roomWalls(room);
    const height = parseMeasure(room.height);
    if (!walls || !height || height.isZero())
        return null;
    const segments = [];
    for (const slope of room.slopes) {
        const parsed = parseSlope(slope, height, walls.length);
        if ('segments' in parsed)
            segments.push(...parsed.segments);
    }
    if (segments.length === 0)
        return null;
    const geometry = contourGeometry(walls);
    return roofGeometry({
        vertices: geometry.vertices,
        wallEnds: geometry.vertices.map((_, i) => geometry.vertices[i + 1] ?? geometry.end),
        clockwise: !geometry.signedArea.isNegative(),
        height,
        segments,
    });
}
export function calcRoom(room) {
    const length = orZero(room.length);
    const width = orZero(room.width);
    const height = orZero(room.height);
    // Ручное поле, если оно включено, перекрывает расчёт даже пустым (тогда 0):
    // иначе пустая «Площадь пола» молча бралась бы из длины и ширины, оставшихся
    // от прямоугольного режима и скрытых от прораба.
    // Включённый, но ещё не нарисованный контур (пустой массив) даёт нули —
    // по той же причине, что и пустые ручные поля.
    const contour = room.contour != null ? contourGeometry(room.contour) : null;
    const floorArea = contour?.area ?? (room.floorAreaOverride != null ? orZero(room.floorAreaOverride) : length.times(width));
    const perimeter = contour?.perimeter ??
        (room.perimeterOverride != null ? orZero(room.perimeterOverride) : length.plus(width).times(2));
    let openingsArea = ZERO;
    let doorWidths = ZERO;
    let slopesLength = ZERO;
    for (const opening of room.openings) {
        const w = orZero(opening.width);
        const h = orZero(opening.height);
        const count = orZero(opening.count);
        openingsArea = openingsArea.plus(w.times(h).times(count));
        // Откос — две боковые стороны и верх проёма. Низ окна — подоконник,
        // у двери — порог; это отдельные работы и в откосы не входят.
        slopesLength = slopesLength.plus(h.times(2).plus(w).times(count));
        if (opening.kind === 'DOOR')
            doorWidths = doorWidths.plus(w.times(count));
    }
    // У мансарды стены ниже высоты помещения, а потолок больше пола — оба
    // берутся из геометрии скатов.
    const roof = roomRoof(room);
    const grossWallArea = roof?.wallArea ?? perimeter.times(height);
    // Стены считаются за вычетом проёмов — так принято в сметах на отделку.
    // Ошибочно большие проёмы не должны давать отрицательную площадь.
    const wallArea = Decimal.max(grossWallArea.minus(openingsArea), ZERO);
    // Плинтус в дверном проёме не кладут.
    const baseboardLength = Decimal.max(perimeter.minus(doorWidths), ZERO);
    return {
        FLOOR_AREA: floorArea,
        CEILING_AREA: roof?.ceilingArea ?? floorArea,
        CEILING_SLOPE_AREA: roof?.slopeArea ?? ZERO,
        WALL_AREA: wallArea,
        PERIMETER: perimeter,
        BASEBOARD_LENGTH: baseboardLength,
        SLOPES_LENGTH: slopesLength,
        OPENINGS_AREA: openingsArea,
    };
}
/** Сумма показателей по выбранным помещениям. Пустой список id — все помещения. */
export function sumRooms(rooms, roomIds = []) {
    const selected = roomIds.length > 0 ? rooms.filter((r) => roomIds.includes(r.id)) : rooms;
    const totals = Object.fromEntries(MEASURE_METRICS.map((m) => [m, ZERO]));
    for (const room of selected) {
        const measures = calcRoom(room);
        for (const metric of MEASURE_METRICS)
            totals[metric] = totals[metric].plus(measures[metric]);
    }
    return totals;
}
/**
 * Объём строки сметы по обмерам. Запас применяется к сумме по помещениям,
 * округление — один раз в конце, до точности количества в документах (3 знака).
 */
export function measuredQuantity(input) {
    const base = sumRooms(input.rooms, input.roomIds)[input.metric];
    const waste = parseMeasure(input.wastePercent) ?? ZERO;
    return roundQuantity(base.times(waste.dividedBy(100).plus(1)));
}
//# sourceMappingURL=measurements.js.map